/*!
 * Copyright © 2025. Verizon Connect Ireland Limited. All rights reserved.
 */

import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, Params, Resolve, Router, RouterStateSnapshot } from '@angular/router';
import { select, Store } from '@ngrx/store';
import { Observable, of, throwError } from 'rxjs';
import { catchError, filter, switchMap, take } from 'rxjs/operators';
import { isElementNull } from '../../../../utils';
import { ROUTE_PARAMS } from '../../../core/constants/route-params.constants';
import { IGeofencePlot, IHotspotPlot, LatLng } from '../../../core/models';
import { PlacesHttpService } from '../../../core/services/places-http/places-http.service';
import { TriggerCreateGeofence, TriggerEditPlace } from '../../../core/store/actions';
import { getShowStartingLocation } from '../../../core/store/selectors/maps.selectors';
import { IAppState } from '../../../core/store/state';
import { EPlaceMode, IPlaceQueryParams } from '../../models';
import { GoogleMapsLoaderService } from '../../services';

@Injectable()
export class PlaceByQueryParamResolver implements Resolve<boolean> {
  constructor(
    private readonly _placeService: PlacesHttpService,
    private readonly _store: Store<IAppState>,
    private readonly _router: Router,
    private readonly _googleMapsLoaderService: GoogleMapsLoaderService
  ) {}

  resolve(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean> {
    const queryParams = this._extractQueryParams(route.queryParams);
    const mode = route.data['mode'] as string;

    switch (mode) {
      case EPlaceMode.EDIT:
        return this._handleEditPlace(queryParams);
      case EPlaceMode.CREATE:
      default:
        return this._handleCreatePlace(queryParams);
    }
  }

  private _handleEditPlace(queryParams: IPlaceQueryParams): Observable<boolean> {
    const geofenceId = this._parseId(queryParams.geofenceId);
    const suggestedGeofenceId = this._parseId(queryParams.suggestedGeofenceId);

    return this._getPlotDetails(geofenceId, suggestedGeofenceId).pipe(
      switchMap(plot => this._validateAndTriggerEdit(plot)),
      catchError(error => this._handleError(error))
    );
  }

  private _validateAndTriggerEdit(plot: IGeofencePlot | IHotspotPlot | undefined): Observable<boolean> {
    if (!plot || plot.id === 0) {
      return throwError(() => new Error('Invalid plot data'));
    }
    return this._executeGeofenceAction(() => {
      this._store.dispatch(new TriggerEditPlace({ id: plot.id, type: plot.type }));
    });
  }

  private _getPlotDetails(geofenceId: number, suggestedGeofenceId: number): Observable<IGeofencePlot | IHotspotPlot | undefined> {
    if (geofenceId) {
      return this._placeService.getGeofencePlotDetails(geofenceId);
    }
    if (suggestedGeofenceId) {
      return this._placeService.getHotspotPlotDetails(suggestedGeofenceId);
    }
    return of(undefined);
  }

  private _handleCreatePlace(queryParams: IPlaceQueryParams): Observable<boolean> {
    const coordinates = this._parseCoordinates(queryParams);
    return this._executeGeofenceAction(() => {
      this._store.dispatch(new TriggerCreateGeofence(coordinates));
    }).pipe(catchError(error => this._handleError(error)));
  }

  private _parseId(value: string | undefined): number {
    return value ? +value : 0;
  }

  private _parseCoordinates(queryParams: IPlaceQueryParams): LatLng {
    const lat = queryParams.lat ? parseFloat(queryParams.lat) : 0;
    const lng = queryParams.lng ? parseFloat(queryParams.lng) : 0;
    return new LatLng(lat, lng);
  }

  private _extractQueryParams(queryParams: Params): IPlaceQueryParams {
    return {
      geofenceId: queryParams[ROUTE_PARAMS.PLACE.GEOFENCE_ID],
      suggestedGeofenceId: queryParams[ROUTE_PARAMS.PLACE.SUGGESTED_GEOFENCE_ID],
      lat: queryParams[ROUTE_PARAMS.PLACE.LAT],
      lng: queryParams[ROUTE_PARAMS.PLACE.LNG]
    };
  }

  private _executeGeofenceAction(action: () => void): Observable<boolean> {
    return this._store.pipe(
      select(getShowStartingLocation),
      filter(location => !isElementNull(location)),
      take(1),
      switchMap(() => {
        this._googleMapsLoaderService.googleMapsLoaded$
          .pipe(
            filter(isLoaded => isLoaded),
            take(1)
          )
          .subscribe(() => action());
        return of(true);
      })
    );
  }

  private _handleError(error: unknown): Observable<boolean> {
    this._router.navigate(['/']);
    return of(false);
  }
}
